import React, { useState, useEffect } from 'react';
import './App.css';
import config from './config';
import { apiFetch } from './api-endpoint';
import {
  getInitialLang, saveLang, getInitialThemeMode, saveThemeMode, resolveTheme, withLang, strings, tplText,
} from './i18n';

// 模板列表本地缓存：进页面先渲染上次结果，网络返回后原地更新，避免"加载中→卡片"的布局跳动
const TEMPLATE_CACHE_KEY = 'soda-templates';

function readTemplateCache() {
  try {
    const raw = window.localStorage.getItem(TEMPLATE_CACHE_KEY);
    const list = raw ? JSON.parse(raw) : null;
    return Array.isArray(list) ? list.filter((t) => t && t.id && t.name) : [];
  } catch (e) {
    return [];
  }
}

function writeTemplateCache(list) {
  try { window.localStorage.setItem(TEMPLATE_CACHE_KEY, JSON.stringify(list)); } catch (e) { /* 隐私模式忽略 */ }
}

function CodePreviewModal({ visible, onClose, fileName, fileContent }) {
  if (!visible) return null;
  return (
    <div className="code-modal-overlay" onClick={onClose}>
      <div className="code-modal" onClick={e => e.stopPropagation()}>
        <button className="code-modal-close" onClick={onClose}>×</button>
        <div className="code-modal-header">{fileName}</div>
        <div className="code-modal-content" onWheel={e => e.stopPropagation()}>
          <pre>{fileContent}</pre>
        </div>
      </div>
    </div>
  );
}

function ThemeToggle({ t }) {
  const [mode, setMode] = useState(getInitialThemeMode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolveTheme(mode));
    saveThemeMode(mode);
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)');
    if (!mq) return undefined;
    const onChange = () => {
      if (getInitialThemeMode() === 'auto') {
        document.documentElement.setAttribute('data-theme', resolveTheme('auto'));
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  return (
    <span className="theme-toggle" role="group" aria-label="theme">
      {[['auto', t('themeAuto')], ['light', t('themeLight')], ['dark', t('themeDark')]].map(([value, label]) => (
        <button
          key={value}
          type="button"
          className={`theme-opt ${mode === value ? 'on' : ''}`}
          onClick={() => setMode(value)}
        >
          {label}
        </button>
      ))}
    </span>
  );
}

function App() {
  const [packageName, setPackageName] = useState('');
  const [projectName, setProjectName] = useState('');
  // 有缓存时首屏就选中第一个模板，真实数据回来后若仍有效则保持不动
  const [templateType, setTemplateType] = useState(() => (readTemplateCache()[0]?.id || ''));
  const [language, setLanguage] = useState('zh');
  const [springBootVersion, setSpringBootVersion] = useState(() => {
    const tpl = readTemplateCache()[0];
    return tpl?.config?.spring_boot_version?.default || '4.0';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileTree, setFileTree] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [logs, setLogs] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  // 模板列表：有缓存则首屏立即渲染真卡片（含选中态），无缓存时显示骨架屏
  const [templates, setTemplates] = useState(readTemplateCache);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [showCodeModal, setShowCodeModal] = useState(false);

  // 界面语言：URL ?lang= → Cookie(.hibuka.com) → localStorage → 浏览器语言
  const [lang, setLang] = useState(getInitialLang);
  const t = (key) => (strings[lang] && strings[lang][key]) || key;

  useEffect(() => {
    document.title = t('title');
    document.documentElement.lang = lang === 'en' ? 'en' : 'zh-CN';
  }, [lang]);

  const toggleLang = () => {
    const next = lang === 'zh' ? 'en' : 'zh';
    saveLang(next);
    setLang(next);
  };

  // 添加验证错误状态（存文案 key，语言切换后即时跟随）
  const [packageNameError, setPackageNameError] = useState('');
  const [projectNameError, setProjectNameError] = useState('');

  // 包名验证函数
  const validatePackageName = (value) => {
    if (!value) {
      return 'errPackageRequired';
    }

    // 包名格式验证：必须使用点号分隔，不能包含特殊字符
    const packageNameRegex = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)*$/;
    if (!packageNameRegex.test(value)) {
      return 'errPackageFormat';
    }

    // 检查是否以点号开头或结尾
    if (value.startsWith('.') || value.endsWith('.')) {
      return 'errPackageDotEdge';
    }

    // 检查连续点号
    if (value.includes('..')) {
      return 'errPackageDotSeq';
    }

    // 检查每个部分是否为空
    const parts = value.split('.');
    for (let part of parts) {
      if (!part) {
        return 'errPackageEmptyPart';
      }
    }

    return '';
  };

  // 项目名称验证函数
  const validateProjectName = (value) => {
    if (!value) {
      return 'errProjectRequired';
    }

    // 项目名称格式验证：允许字母、数字、连字符、下划线
    const projectNameRegex = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
    if (!projectNameRegex.test(value)) {
      return 'errProjectFormat';
    }

    // 检查长度
    if (value.length < 2) {
      return 'errProjectMin';
    }

    if (value.length > 50) {
      return 'errProjectMax';
    }

    return '';
  };

  // 处理包名变化
  const handlePackageNameChange = (value) => {
    setPackageName(value);
    const error = validatePackageName(value);
    setPackageNameError(error);
    setError(''); // 清除全局错误
  };

  // 处理项目名称变化
  const handleProjectNameChange = (value) => {
    setProjectName(value);
    const error = validateProjectName(value);
    setProjectNameError(error);
    setError(''); // 清除全局错误
  };

  // 验证所有输入
  const validateAll = () => {
    const packageError = validatePackageName(packageName);
    const projectError = validateProjectName(projectName);
    
    setPackageNameError(packageError);
    setProjectNameError(projectError);
    
    return !packageError && !projectError;
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // 当模板类型改变时，自动刷新预览
  useEffect(() => {
    if (templateType && showPreview && packageName && projectName) {
      handlePreview();
    }
  }, [templateType]);

  // 模板列表变化后兜底选中：已有选中项仍有效则保持（缓存秒出后用户可能已点选），否则选第一个
  useEffect(() => {
    if (templates.length === 0) return;
    const kept = templates.find((tpl) => tpl.id === templateType);
    if (!kept) {
      const tpl = templates[0];
      setTemplateType(tpl.id);
      const versions = tpl.config?.spring_boot_version?.choices;
      const def = tpl.config?.spring_boot_version?.default;
      if (versions?.length) setSpringBootVersion(def || versions[0]);
    }
  }, [templates]);

  const loadTemplates = async () => {
    try {
      const path = '/listTemplates';
      const body = JSON.stringify({});

      const response = await apiFetch(path, { method: 'POST', body });
      const data = await response.json();
      
      if (data.success) {
        setTemplates(data.data);
        writeTemplateCache(data.data);
      } else {
        addLog('加载模板列表失败: ' + data.message, 'error');
      }
    } catch (err) {
      addLog('加载模板列表失败: ' + err.message, 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const handlePreview = async () => {
    // 先验证输入
    if (!validateAll()) {
      setError(t('errCheckPreview'));
      return;
    }

    setIsLoading(true);
    setError('');
    addLog('开始生成项目预览...', 'info');

    try {
      const path = '/previewTemplate';
      const body = JSON.stringify({
        packageName: packageName,
        projectName: projectName,
        templateType: templateType,
        language: language,
        springBootVersion: springBootVersion,
      });

      const response = await apiFetch(path, { method: 'POST', body });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || t('errPreview'));
      }

      setFileTree(data.data.fileTree);
      setShowPreview(true);
      setExpandedNodes(new Set()); // 重置展开状态
      setSelectedFile(null); // 清空选中的文件
      setFileContent(''); // 清空文件内容
      addLog('项目预览生成成功', 'success');
      addLog(`共生成 ${countFiles(data.data.fileTree)} 个文件`, 'info');
    } catch (err) {
      setError(t('errPreview') + err.message);
      addLog('预览失败: ' + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const countFiles = (nodes) => {
    let count = 0;
    for (const node of nodes) {
      if (node.directory) {
        count += countFiles(node.children);
      } else {
        count++;
      }
    }
    return count;
  };

  const handleViewFile = async (filePath) => {
    setSelectedFile(filePath);
    addLog(`正在读取文件: ${filePath}`, 'info');

    try {
      const path = '/viewFile';
      const body = JSON.stringify({
        packageName: packageName,
        projectName: projectName,
        templateType: templateType,
        filePath: filePath,
        language: language,
        springBootVersion: springBootVersion,
      });

      const response = await apiFetch(path, { method: 'POST', body });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || t('errReadFile'));
      }

      setFileContent(data.data);
      addLog(`文件读取成功: ${filePath}`, 'success');
    } catch (err) {
      setError(t('errReadFile') + err.message);
      addLog('读取文件失败: ' + err.message, 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 先验证输入
    if (!validateAll()) {
      setError(t('errCheckGenerate'));
      return;
    }
    
    setIsLoading(true);
    setError('');
    addLog('开始生成项目...', 'info');

    try {
      const path = '/generate';
      const body = JSON.stringify({
        packageName: packageName,
        projectName: projectName,
        templateType: templateType,
        language: language,
        springBootVersion: springBootVersion,
      });

      const response = await apiFetch(path, { method: 'POST', body });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName || 'project'}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      addLog('项目生成并下载成功', 'success');
    } catch (err) {
      setError(t('errGenerate') + err.message);
      addLog('生成项目失败: ' + err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNode = (nodePath) => {
    const newExpandedNodes = new Set(expandedNodes);
    if (newExpandedNodes.has(nodePath)) {
      newExpandedNodes.delete(nodePath);
    } else {
      newExpandedNodes.add(nodePath);
    }
    setExpandedNodes(newExpandedNodes);
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    const iconMap = {
      'java': '☕',
      'xml': '📄',
      'properties': '⚙️',
      'yml': '📋',
      'yaml': '📋',
      'md': '📝',
      'txt': '📄',
      'json': '📋',
      'js': '📜',
      'css': '🎨',
      'html': '🌐',
      'sql': '🗄️',
      'sh': '💻',
      'bat': '💻',
      'gradle': '🔧',
      'gitignore': '🚫',
      'pom': '📦'
    };
    return iconMap[ext] || '📄';
  };

  const getFileTypeClass = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    const typeMap = {
      'java': 'file-java',
      'xml': 'file-xml',
      'properties': 'file-config',
      'yml': 'file-config',
      'yaml': 'file-config',
      'md': 'file-doc',
      'txt': 'file-text',
      'json': 'file-data',
      'js': 'file-script',
      'css': 'file-style',
      'html': 'file-web',
      'sql': 'file-db',
      'sh': 'file-script',
      'bat': 'file-script',
      'gradle': 'file-build',
      'gitignore': 'file-git',
      'pom': 'file-build'
    };
    return typeMap[ext] || 'file-default';
  };

  const renderFileTree = (nodes, level = 0, parentPath = '') => {
    return nodes.map((node, index) => {
      const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
      const isExpanded = expandedNodes.has(nodePath);
      
      return (
        <div key={index} className="tree-node">
          <div 
            className={`file-node ${node.directory ? 'directory' : 'file'} ${node.directory ? '' : getFileTypeClass(node.name)}`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
          >
            {node.directory && (
              <span 
                className="expand-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(nodePath);
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            
            <span className="icon">
              {node.directory ? '📁' : getFileIcon(node.name)}
            </span>
            
            <span 
              className="name"
              onClick={() => !node.directory && handleViewFile(node.path)}
            >
              {node.name}
            </span>
            
            {!node.directory && (
              <span className="size">({formatFileSize(node.size)})</span>
            )}
          </div>
          
          {node.directory && node.children && isExpanded && (
            <div className="children">
              {renderFileTree(node.children, level + 1, nodePath)}
            </div>
          )}
        </div>
      );
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSelectedTemplate = () => {
    return templates.find((tpl) => tpl.id === templateType);
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-in">
          <div className="header-left">
            <a className="logo" href={withLang('https://soda.hibuka.com/', lang)} title="SODA">
              <img className="logo-mark" src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" />
              <span>soda</span>
            </a>
            <span className="logo-sub">{t('navProject')}</span>
          </div>
          <div className="header-right">
            <nav className="header-nav">
              <a className="nav-link" href={withLang('https://soda.hibuka.com/', lang)}>{t('navProduct')}</a>
              <a className="nav-link" href={withLang('https://docs.soda.hibuka.com/', lang)}>{t('navDocs')}</a>
              <a
                className="nav-link"
                href={config.app.github.url}
                target="_blank"
                rel="noopener noreferrer"
                title={config.app.github.description}
              >
                GitHub ↗
              </a>
              <a className="nav-link lang-toggle" onClick={toggleLang}>
                {lang === 'zh' ? 'English' : '中文'}
              </a>
              <ThemeToggle t={t} />
            </nav>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="hero">
          <h1 className="hero-title"><span className="hero-brand">soda</span> {t('heroTitle')}</h1>
          <p className="hero-sub">{t('desc')}</p>
        </div>

        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label htmlFor="packageName">{t('labelPackage')}</label>
            <input
              type="text"
              id="packageName"
              value={packageName}
              onChange={(e) => handlePackageNameChange(e.target.value)}
              placeholder={t('phPackage')}
              className={packageNameError ? 'error-input' : ''}
              required
            />
            {packageNameError && (
              <div className="error">{t(packageNameError)}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="projectName">{t('labelProject')}</label>
            <input
              type="text"
              id="projectName"
              value={projectName}
              onChange={(e) => handleProjectNameChange(e.target.value)}
              placeholder={t('phProject')}
              className={projectNameError ? 'error-input' : ''}
              required
            />
            {projectNameError && (
              <div className="error">{t(projectNameError)}</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="templateType">{t('labelTemplate')}</label>
            {templates.length > 0 ? (
              <div className="template-selector">
                {templates.map((template) => {
                  const txt = tplText(template, lang);
                  return (
                  <div
                    key={template.id}
                    className={`template-option ${templateType === template.id ? 'selected' : ''}`}
                    onClick={() => {
                      setTemplateType(template.id);
                      const versions = template.config?.spring_boot_version?.choices;
                      const def = template.config?.spring_boot_version?.default;
                      if (versions?.length) setSpringBootVersion(def || versions[0]);
                    }}
                  >
                    <div className="template-icon">{template.icon}</div>
                    <div className="template-info">
                      <div className="template-name">{txt.name}</div>
                      <div className="template-description">{txt.description}</div>
                      <div className="template-tags">
                        {template.tags && template.tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : loadingTemplates ? (
              // 骨架屏：占位与真卡片同尺寸，数据到达后原位替换，避免布局跳动
              <div className="template-selector" aria-hidden="true">
                {[0, 1].map((i) => (
                  <div key={i} className="template-option template-skeleton">
                    <div className="template-icon"><span className="skeleton-icon" /></div>
                    <div className="template-info">
                      <span className="skeleton-line w-40" />
                      <span className="skeleton-line w-90" />
                      <span className="skeleton-line w-55" />
                      <div className="template-tags">
                        <span className="skeleton-tag" />
                        <span className="skeleton-tag" />
                        <span className="skeleton-tag" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="loading-templates">{t('tplLoadFailed')}</div>
            )}
          </div>

          <div className="form-group">
            <label>{t('labelCommentLang')}</label>
            <div className="radio-group horizontal">
              <label className="radio-option">
                <input
                  type="radio"
                  name="language"
                  value="zh"
                  checked={language === 'zh'}
                  onChange={(e) => setLanguage(e.target.value)}
                />
                <span className="radio-label">中文</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="language"
                  value="en"
                  checked={language === 'en'}
                  onChange={(e) => setLanguage(e.target.value)}
                />
                <span className="radio-label">English</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>{t('labelBootVersion')}</label>
            <div className="radio-group horizontal">
              {(getSelectedTemplate()?.config?.spring_boot_version?.choices || ['4.0', '3.2']).map((version) => (
                <label className="radio-option" key={version}>
                  <input
                    type="radio"
                    name="springBootVersion"
                    value={version}
                    checked={springBootVersion === version}
                    onChange={(e) => setSpringBootVersion(e.target.value)}
                  />
                  <span className="radio-label">{version}.x</span>
                </label>
              ))}
            </div>
          </div>

          {/* 隐藏模板详情
          {getSelectedTemplate() && (
            <div className="template-details">
              <h4>模板详情</h4>
              <div className="template-features">
                <strong>功能特性：</strong>
                <ul>
                  {getSelectedTemplate().features && getSelectedTemplate().features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="template-dependencies">
                <strong>主要依赖：</strong>
                <div className="dependency-tags">
                  {getSelectedTemplate().dependencies && getSelectedTemplate().dependencies.map((dep, index) => (
                    <span key={index} className="dependency-tag">{dep}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          */}
          
          <div className="button-group">
            <button
              type="button"
              onClick={handlePreview}
              disabled={isLoading || loadingTemplates}
              className="preview-btn"
            >
              {isLoading ? t('btnPreviewBusy') : t('btnPreview')}
            </button>

            <button
              type="submit"
              disabled={isLoading || loadingTemplates}
              className="submit-btn"
            >
              {isLoading ? t('btnSubmitBusy') : t('btnSubmit')}
            </button>
          </div>
        </form>
        
        {error && (
          <div className="error">
            {error}
          </div>
        )}

        {showPreview && fileTree && (
          <div className="preview-section">
            <h2>{t('previewTitle')}</h2>
            <div className="preview-container">
              <div className="file-tree">
                <h3>{t('previewTree')}</h3>
                <div className="tree-container">
                  {renderFileTree(fileTree)}
                </div>
              </div>

              {selectedFile && (
                <div className="file-content">
                  <h3>{t('previewFile')}{selectedFile.split('/').pop()}</h3>
                  <div className="code-container">
                    <pre className="code-block">{fileContent}</pre>
                    <button className="fullscreen-btn" onClick={() => setShowCodeModal(true)} title={t('fullscreen')}>⛶</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 代码浏览弹窗 */}
        {showCodeModal && (
          <div className="modal-overlay" onClick={() => setShowCodeModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="soda-modal-title">{t('previewFile')}{selectedFile && selectedFile.split('/').pop()}</h3>
                <button className="modal-close" onClick={() => setShowCodeModal(false)}>✕</button>
              </div>
              <div className="modal-body" style={{padding: 0}}>
                <div style={{maxHeight: '70vh', overflow: 'auto', background: '#23272e', borderRadius: 10, padding: 24}}>
                  <pre style={{color: '#f8f8f2', fontSize: '1.08em', margin: 0, padding: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'Fira Mono, Menlo, Consolas, monospace'}}>{fileContent}</pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 隐藏操作日志
        <div className="logs-section">
          <h3>操作日志</h3>
          <div className="logs-container">
            {logs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.type}`}>
                <span className="timestamp">[{log.timestamp}]</span>
                <span className="message">{log.message}</span>
              </div>
            ))}
            {logs.length === 0 && (
              <div className="no-logs">暂无日志</div>
            )}
          </div>
        </div>
        */}
      </div>

      {/* 页面底部 - 项目信息和备案 */}
      <footer className="app-footer">
        <div className="footer-content">
          <a className="logo footer-logo" href={withLang('https://soda.hibuka.com/', lang)}>
            <img className="logo-mark" src={`${process.env.PUBLIC_URL}/logo.svg`} alt="" />
            <span>soda</span>
          </a>
          <div className="footer-links">
            <a href={withLang('https://soda.hibuka.com/', lang)}>{t('footerProduct')}</a>
            <a href={withLang('https://docs.soda.hibuka.com/', lang)}>{t('footerDocs')}</a>
            <a href={config.app.github.url} target="_blank" rel="noopener noreferrer" title={config.app.github.description}>
              GitHub
            </a>
          </div>
          <div className="footer-copyright">
            <p>{t('footerCopy')} · MIT License</p>
            <a href="mailto:soda@hibuka.com">soda@hibuka.com</a>
            <a href={config.app.icpUrl} target="_blank" rel="noopener noreferrer">
              {config.app.icp}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 